import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import type { ComponentType, FormEvent } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { prefetchRoute, prefetchRouteChunk } from "@/lib/routePrefetch";
import OrbIcon from "@/components/OrbIcon";
import {
  Activity,
  BarChart3,
  BookOpen,
  Bot,
  Eye,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  Megaphone,
  Menu,
  MessageCircleQuestion,
  Package,
  Plug,
  Repeat2,
  Rocket,
  ScrollText,
  Settings,
  Gauge,
  ShieldCheck,
  ShoppingBag,
  Users,
  X,
} from "lucide-react";

import { clearSession, getOperator, getTenantId, isImpersonating, setTenantId } from "@/lib/auth";
import { formatNavCount, useNavCounts } from "@/lib/navCounts";
import { useDashboardConfig } from "@/lib/dashboardConfig";
import { applyTenantAttr } from "@/lib/theme";
import { logout } from "@/lib/api";
import { useTenantStatus } from "@/lib/tenant";
import { usePermissions } from "@/lib/permissions";
import type { Permission } from "@/lib/rbac";
import { CreditBalanceBanner } from "@/components/CreditBalanceBanner";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { NotificationToaster } from "@/components/notifications/NotificationToaster";
// §pilot-review — "Report a problem" (shell-level, every page): free text +
// auto-attached route/user-agent/console-error context. No screenshots by
// design (privacy + size).
import { reportProblem } from "@/lib/review";
import {
  installProblemReportBuffers,
  recentConsoleErrors,
  recentConversationRefs,
} from "@/lib/problemReport";
import { AppCanvas } from "@/components/glass";
import { AgentPowerSwitch, AgentPowerStatus } from "@/components/AgentPowerSwitch";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SoftwareUpdateDot } from "@/pages/SoftwareUpdates";
import orikinWordmark from "@/assets/brand/orikin-wordmark-on-dark.png";
import { getBranding } from "@/pages/agent-studio/persona";
import { applyBrandPrimary } from "@/lib/theme";

/**
 * §v3.kit (2026-05-01) — AppShell rewritten against the Expannd
 * Design System 2 component classes. Same React behaviour as the
 * previous version (NavLink routing, persona-aware sidebar filter,
 * theme toggle, logout flow) but the visual surface is now the
 * canonical kit recipe: ``.sidebar.kit-glass`` rail with
 * ``.sb-brand``/``.sb-search``/``.sb-section``/``.sb-item`` rows and
 * a ``.sb-profile`` tenant card at the bottom.
 *
 * Token + class definitions live in:
 *   - ``src/styles/xpand-design.css`` (canonical tokens — canvas,
 *     glass, fg, semantic, channel, tenant accent)
 *   - ``src/styles/kit.css`` (component classes — ported from
 *     the design system's ``ui_kits/dashboard/styles.css``)
 *
 * Layout shape per the kit: 248px rail, 14px outer padding +
 * 14px gap, single 1fr main column. Stays identical at all
 * viewport widths >= 1024px (the laptop-first target).
 */

/** §xpand-ai-orb (2026-07-27) — icons are lucide glyphs, except the XPAND AI
 *  entry which renders the animated orb. Both satisfy this shape. */
type NavIcon = ComponentType<{
  className?: string;
  size?: number;
  "aria-hidden"?: boolean | "true" | "false";
}>;

type NavItem = {
  to: string;
  label: string;
  icon: NavIcon;
  /** §v4.nav-counts — show the live item count from useNavCounts on the
   * right of the label (replaces the old decorative ⌘N hints — no global
   * key handler ever shipped, so the hints promised nothing real). */
  count?: boolean;
  /** §rbac — gate this item on a single permission. */
  permission?: Permission;
};

type NavSection = {
  id: string;
  label: string;
  items: NavItem[];
};

const NAV: NavSection[] = [
  // §xpand-ai (2026-06-09) — the copilot sits at the very top, directly below
  // the brand wordmark, as the primary "ask anything" entry. Empty section
  // label so it renders flush under the wordmark with no group header.
  {
    id: "xpand-ai",
    label: "",
    items: [
      { to: "/ask", label: "AI Agent", icon: OrbIcon, permission: "overview.view" },
    ],
  },
  {
    id: "today",
    label: "Today",
    items: [
      { to: "/overview", label: "Overview", icon: LayoutDashboard, permission: "overview.view" },
      // §v3.drop-conversations (2026-05-08) — Conversations
      // (list at /inbox) sidebar entry removed; the unified list
      // was redundant once Live calls / Enquiries / Orders /
      // Support each owned their own slice. The /inbox/:id
      // DETAIL route stays in App.tsx so deep links from Order
      // detail, Support detail, Timeline, and Overview keep
      // working — only the browse list is gone.
      { to: "/live-calls", label: "Live monitor", icon: Activity, count: true, permission: "live_calls.view" },
      // §v3.enquiries (2026-05-08) — sits between Live calls and
      // Orders because it's a pre-conversion bucket: customer
      // engaged but didn't convert. Operator's eye flow:
      // live → enquiry pile → orders / support. Shares the
      // ``conversations.view`` permission since it's a view over
      // the same conversation table.
      { to: "/enquiries", label: "Enquiries", icon: MessageCircleQuestion, count: true, permission: "conversations.view" },
      { to: "/orders", label: "Orders", icon: ShoppingBag, count: true, permission: "orders.view" },
      // §approval-wiring (2026-08-07) — SHOWN, per the deal this entry's own
      // hidden-state comment set on 2026-08-06: "re-show this line the moment
      // the first tool is gated." send_payment_link and create_order now run
      // through action_policy.decide() with registered executors, so a row in
      // this queue is a real prepared action and Approve genuinely runs it.
      { to: "/approvals", label: "Approvals", icon: ShieldCheck, count: true, permission: "orders.view" },
      { to: "/support", label: "Support", icon: LifeBuoy, count: true, permission: "support.view" },
      { to: "/customers", label: "Customers", icon: Users, count: true, permission: "customers.view" },
      // §b2b Stage E — Accounts + Invoices nav HIDDEN for now (Akil 2026-06-17):
      // the B2B-accounts engine stays live (routes /accounts + /invoices still
      // work by URL, agent auto-link + contract pricing + credit invoicing + AR
      // all function), but the tabs aren't surfaced until a real B2B/wholesale
      // customer needs them. Re-show by restoring these two items (b2bOnly:true)
      // + the Briefcase/Receipt imports.
    ],
  },
  // §v3.knowledge-group (2026-05-11) — the three surfaces the voice
  // agent reads at runtime were previously split: Knowledge base
  // sat under "Today" while Brand and Routing sat under "Agent".
  // That split forced operators to guess where to edit an FAQ
  // (Brand vs Q&A?) or a product mapping (Routing vs Products?).
  // Consolidating into one "Knowledge" section keeps the items
  // separate (sidebar discoverability) but groups them by mental
  // model: "everything the agent reads to answer the customer".
  // URLs unchanged so deep links / bookmarks / shortcut 7 keep
  // working. Historical §v3.rename-products (2026-05-08) /
  // §v3.brand (2026-05-11) / §v3.routing (2026-05-11) context
  // preserved in git history.
  {
    id: "knowledge",
    label: "Knowledge",
    items: [
      // §v3.kb-merge (2026-05-12) — Brand facts + Custom Q&A
      // collapsed into one Knowledge entry. The page itself has
      // two tabs ("Canonical answers" / "Custom Q&A"). The old
      // /brand and /products/qa routes still resolve (they
      // redirect to /knowledge and /knowledge/qa respectively),
      // so existing bookmarks and shortcut wiring don't break.
      { to: "/knowledge", label: "Knowledge", icon: BookOpen, permission: "settings.view" },
      // Products is now just products. Q&A moved out into the
      // Knowledge page next to the canonical answers it
      // conceptually belongs with.
      // §stock-alert-badge — counts unacknowledged stock ALERTS, not items.
      { to: "/products", label: "Products", icon: Package, count: true, permission: "products.view" },
    ],
  },
  {
    id: "agent",
    label: "Agent",
    items: [
      // §v3.agent-studio (2026-06-01) — "Agent voice", "Training" and
      // "AI Simulation" were three separate items for what is really one
      // workflow (configure → test → train the agent). Consolidated into a
      // single "Agent Studio" entry with Persona / Simulate / Train tabs.
      // The old /agent, /training, /ai-simulation paths redirect into the
      // matching tab so bookmarks + shortcut 9 keep working.
      { to: "/agent-studio", label: "Agent Studio", icon: Bot, permission: "agent.view" },
      // §channels-page (2026-08-31) — the self-serve storefront of places the
      // agent answers: honest per-channel status + connect-it-yourself flows.
      // Lives in the Agent group because a channel is where the agent works.
      { to: "/channels", label: "Channels", icon: Plug, permission: "settings.view" },
      // §agent-qa (2026-06-23) — Agent Reliability: run the deterministic eval
      // battery against the live brain + see the pass score per dimension.
      { to: "/agent-qa", label: "Agent QA", icon: ShieldCheck, permission: "settings.view" },
      // §v3.analytics-retired (2026-05-12) — Analytics surface
      // removed; Reports + Overview cover the same ground.
      { to: "/reports", label: "Reports", icon: BarChart3, permission: "reports.view" },
    ],
  },
  {
    id: "manage",
    label: "Manage",
    items: [
      // §v3.marketing (2026-05-25) — outbound campaigns surface. Gated on
      // operators who can see customers (the Audience builder reads customer
      // data).
      { to: "/marketing", label: "Marketing", icon: Megaphone, permission: "customers.view" },
      // §security.audit-log (2026-06-20) — read-only security audit trail +
      // tamper-evidence check. Gated on audit_log.view (Owner/Admin by default).
      { to: "/audit-log", label: "Audit Log", icon: ScrollText, permission: "audit_log.view" },
      // §usage-billing (2026-07-12) — consumption meter, credits and the
      // subscription lifecycle. count:true → the near-limit warning badge.
      { to: "/usage", label: "Usage & billing", icon: Gauge, count: true, permission: "settings.view" },
      // §v3.settings-hub (2026-05-25) — Users, Roles & permissions, and
      // the operator's own Security now live as tabs UNDER Settings, so
      // the rail carries ONE entry instead of four. No permission gate:
      // the Security tab is universal (every operator manages their own
      // 2FA), so Settings must always be reachable; SettingsLayout hides
      // the individual tabs an operator can't access and lands them on
      // their first allowed one.
      { to: "/settings", label: "Settings", icon: Settings },
    ],
  },
  // §master-panel — platform-admin section. Only renders for the
  // Master Admin role inside the reserved 'master' tenant; all
  // other operators have their permission filter strip every item
  // here, which makes the empty section drop out via the
  // ``items.length > 0`` filter below.
  // §mc-tenant-admin (2026-06-04) — the standalone /master/tenants page was
  // folded into Mission Control (Fleet directory + tenant detail), so this
  // entry now jumps straight to the founder console (own full-screen shell).
  {
    id: "platform",
    label: "Platform",
    items: [
      { to: "/mission-control", label: "Mission Control", icon: Rocket, permission: "master.tenants.view" },
    ],
  },
];

/**
 * §pilot-review — the shell's "Report a problem" modal. Deliberately tiny:
 * one textarea; tenant id rides the X-Tenant-ID header, route/user-agent/
 * console errors/recent conversation refs are attached automatically so the
 * founder can triage from the queue row alone. Chrome mirrors
 * AddCorrectionDialog (no shared Dialog primitive in the codebase yet).
 */
function ReportProblemDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const location = useLocation();
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setText("");
    setSubmitting(false);
    setDone(false);
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, submitting, onClose]);

  if (!open) return null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await reportProblem({
        description: text.trim(),
        route: location.pathname,
        user_agent: navigator.userAgent,
        console_errors: recentConsoleErrors(),
        conversation_refs: recentConversationRefs(),
      });
      setDone(true);
      window.setTimeout(onClose, 1600);
    } catch {
      setError("Couldn't send the report. Try again.");
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Report a problem"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
      className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-lg border border-border-subtle bg-surface-raised shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-3 border-b border-border-subtle px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">Report a problem</h2>
            <p className="mt-1 text-sm text-text-secondary">
              Goes straight to the ORIKIN AI team with your current page and
              recent errors attached — no screenshots.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md p-1 text-text-tertiary hover:text-text-primary"
          >
            <X size={16} aria-hidden />
          </button>
        </header>
        {done ? (
          <div className="px-5 py-6 text-sm text-text-primary">
            Thank you — the report is on its way.
          </div>
        ) : (
          <form onSubmit={(e) => void submit(e)} className="px-5 py-4">
            <label className="block text-sm text-text-secondary" htmlFor="report-problem-text">
              What happened?
            </label>
            <textarea
              id="report-problem-text"
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              placeholder="e.g. The orders page shows a spinner forever…"
              className="mt-2 w-full resize-y rounded-md border border-border-subtle bg-transparent px-3 py-2 text-sm text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
            />
            {error ? <div className="mt-2 text-sm text-danger">{error}</div> : null}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={!text.trim() || submitting}>
                {submitting ? "Sending…" : "Send report"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export function AppShell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const operator = getOperator();
  const tenant = getTenantId();
  // §v4.tenant-isolation (2026-06-16) — a master operator viewing a customer
  // console (audited). Drives the persistent exit banner below.
  const impersonating = isImpersonating();
  // §industry-aware-dash B2 (2026-08-08) — the label comes from the tenant's
  // industry pack, not from a tenant-id ladder that only knew two names and
  // showed every pilot tenant a raw slug. roze keeps its historic name;
  // everyone else reads as their industry.
  const dashConfig = useDashboardConfig();
  // §brand-identity (2026-08-20) — apply the tenant's primary colour to the
  // whole shell. AppShell wraps every authenticated page, so setting the
  // `--accent` token here reaches every surface that already respects the
  // theme, including ones written later.
  const brandQ = useQuery({
    queryKey: ["branding"],
    queryFn: getBranding,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
  const brandPrimary = brandQ.data?.brand_primary ?? "";
  useEffect(() => {
    // Re-runs on a workspace switch too. Passing "" REMOVES the override, so
    // the previous tenant's colour cannot linger on the next tenant's shell —
    // that would be a visual cross-tenant leak.
    applyBrandPrimary(brandPrimary);
  }, [brandPrimary]);
  const tenantLabel =
    tenant === "roze"
      ? "Business Agent"
      : dashConfig.key !== "default"
        ? `${dashConfig.industryLabel} agent`
        : tenant;
  const initial = (
    operator?.display_name ?? operator?.username ?? "?"
  )[0]?.toUpperCase();

  // §v3.sidebar-polish (2026-05-01) — hide retail-only nav items for
  // school-receptionist tenants. Persona is exposed by
  // /api/admin/tenant-status; we wait on that and fall back to the
  // full nav while it loads (Roze never sets persona, so an empty
  // value means "show everything", matching legacy behaviour).
  const { data: tenantStatus } = useTenantStatus();
  const persona = tenantStatus?.persona_category ?? "";

  // §rbac (2026-05-08) — filter nav items by the operator's
  // permission set. Items without a `permission` declaration are
  // always visible (login, public pages — the AppShell never
  // renders for those, but we keep the fallback for safety).
  // While /me is still loading, ``has`` returns false for everything,
  // so the rail flashes empty for ~100 ms; preferable to flashing
  // gated items to a viewer for the same window.
  const { has, loading: permsLoading } = usePermissions();

  const navSections = useMemo<NavSection[]>(() => {
    // §industry-aware-dash B2 — ONE shared NAV, re-skinned by the tenant's
    // industry pack: the transactions surface takes the trade's own noun
    // (Orders / Bookings / Appointments / Viewings / Applications), Customers
    // becomes Guests / Patients / Clients, and pack-hidden paths drop out
    // before RBAC.
    let sections = NAV;
    if (dashConfig.key !== "default") {
      const relabel: Record<string, string> = {
        "/orders": dashConfig.ordersLabel,
        "/customers": dashConfig.customersLabel,
      };
      const hidden = new Set(dashConfig.hiddenNavPaths);
      sections = sections
        .map((section) => ({
          ...section,
          items: section.items
            .filter((i) => !hidden.has(i.to))
            .map((i) => (relabel[i.to] ? { ...i, label: relabel[i.to] } : i)),
        }))
        .filter((section) => section.items.length > 0);
    }
    // persona-based filter (school_receptionist hides orders/products/
    // customers).
    if (persona === "school_receptionist") {
      const HIDDEN = new Set(["/orders", "/products", "/customers"]);
      sections = sections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => !HIDDEN.has(item.to)),
        }))
        .filter((section) => section.items.length > 0);
    }
    if (permsLoading) return sections;
    return sections
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item) => !item.permission || has(item.permission),
        ),
      }))
      .filter((section) => section.items.length > 0);
  }, [persona, has, permsLoading, dashConfig]);

  // §v4.nav-counts — live counts for the count-flagged items above.
  // Only the paths that survived the permission filter are fetched.
  const countedPaths = useMemo(
    () =>
      new Set(
        navSections.flatMap((s) =>
          s.items.filter((i) => i.count).map((i) => i.to),
        ),
      ),
    [navSections],
  );
  const navCounts = useNavCounts(countedPaths);

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      clearSession();
      // §rbac — drop every cached query so the next operator's
      // session doesn't render with our stale data (especially
      // the /me permission set, which gates the whole UI).
      queryClient.clear();
      navigate("/login", { replace: true });
    }
  };

  // §v4.tenant-isolation — leave the impersonated customer console: reset
  // X-Tenant-ID to the platform tenant, re-stamp tenant CSS, drop every
  // cached query so /me refetches and re-narrows to master.* perms, then go
  // to Mission Control.
  //
  // (2026-08-13) This used to invalidate, not clear, reasoning that an
  // in-session tenant flip should let billing/notification polls survive.
  // That reasoning was backwards: a poll that survives a tenant flip is
  // precisely what renders the PREVIOUS tenant's numbers, because query
  // keys carry no tenant. Isolation beats a refetch.
  const exitImpersonation = async () => {
    setTenantId("master");
    applyTenantAttr();
    queryClient.clear();
    navigate("/mission-control", { replace: true });
  };

  // §v3.mobile-shell (2026-05-14) — drawer-based nav for phone
  // viewports. Below the kit.css mobile breakpoint (768px), the
  // sidebar becomes an off-canvas drawer that the operator opens
  // via the hamburger button in a sticky top bar. State + scroll
  // lock + ESC + auto-close-on-route-change all live here so the
  // sidebar JSX itself doesn't have to know about it.
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // §pilot-review — install the error ring buffers once (idempotent) and
  // hold the Report-a-problem modal's open state at shell level so the
  // button can live in the quiet profile-card action row.
  const [reportOpen, setReportOpen] = useState(false);
  useEffect(() => {
    installProblemReportBuffers();
  }, []);

  // §v4.nav-glide (2026-07-02) — iOS/iPadOS-pointer-style hover: ONE
  // shared highlight pill that glides between nav items with Apple's
  // spring curve as the cursor (or keyboard focus) moves through the
  // rail, instead of each row lighting up independently. React owns
  // the geometry (top/height measured against the scrollable <nav>);
  // kit.css owns the spring (transform/height transition on .sb-glide).
  const navRef = useRef<HTMLElement>(null);
  const [glide, setGlide] = useState<{ top: number; height: number; on: boolean }>(
    { top: 0, height: 0, on: false },
  );
  const glideTo = (el: HTMLElement) => {
    const nav = navRef.current;
    if (!nav) return;
    const r = el.getBoundingClientRect();
    const nr = nav.getBoundingClientRect();
    setGlide({ top: r.top - nr.top + nav.scrollTop, height: r.height, on: true });
  };
  const glideOff = () => setGlide((g) => ({ ...g, on: false }));

  // Auto-close when the operator navigates (NavLink click → location
  // changes → drawer slides shut). Avoids the "tapped a link, now I
  // can't see the page content" trap on phones.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // ESC key + body scroll lock while the drawer is open.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [drawerOpen]);

  return (
    <div
      className={
        "app-shell-root" + (drawerOpen ? " is-drawer-open" : "")
      }
    >
      {/* §v3 — full-bleed canvas under everything. The kit's recipe
          mounts this once at app root via .canvas-bg; we keep
          AppCanvas (which renders .xpand-canvas-bg, the existing
          equivalent) so the gradient is centralised. */}
      <AppCanvas />

      {/* §v3.mobile-shell — scrim behind the drawer when open. Tap
          to close. position:fixed, only paints on phone breakpoint. */}
      <div
        className={
          "app-drawer-scrim" + (drawerOpen ? " is-visible" : "")
        }
        onClick={() => setDrawerOpen(false)}
        aria-hidden="true"
      />

      <aside
        id="primary-nav-drawer"
        aria-label="Primary"
        className={
          "sidebar kit-glass" + (drawerOpen ? " is-mobile-open" : "")
        }
      >
        {/* §v3.sidebar-wordmark (2026-05-12) — brand area is the
            wordmark PNG, not an icon-tile + text lockup.

            §orikin-brand (2026-08-06) — now the ORIKIN wordmark.
            Asset names say which SURFACE they are for, never which
            colour they are: this sidebar is the dark glass shell, so
            it takes -on-dark (white ink). Picking the wrong one is the
            single mistake that renders a logo invisible, and "-light"
            reads as both "light ink" and "light theme". Tenant caption
            stays underneath as a small mono line.

            §v3.sidebar-search-retired (2026-05-12) — the
            "Search everything ⌘K" placeholder was a stub for a
            ⌘K palette that never shipped. Removed per founder ask
            so it doesn't promise functionality that isn't there. */}
        <div className="sb-brand sb-brand-wordmark">
          <img
            className="sb-wordmark-img"
            src={orikinWordmark}
            alt="ORIKIN AI"
          />
          <span className="sb-wordmark-tenant">Tenant · {tenant}</span>
        </div>

        {/* ── Nav sections ───────────────────────────────────────── */}
        <nav
          ref={navRef}
          style={{ flex: 1, overflowY: "auto", minHeight: 0, position: "relative" }}
          onMouseLeave={glideOff}
        >
          {/* §v4.nav-glide — the shared hover pill. Geometry inline
              (React-measured), spring + paint in kit.css. */}
          <span
            className={"sb-glide" + (glide.on ? " is-on" : "")}
            style={{ transform: `translateY(${glide.top}px)`, height: glide.height }}
            aria-hidden
          />
          {navSections.map((section) => (
            <div key={section.id}>
              {section.label ? <div className="sb-section">{section.label}</div> : null}
              {section.items.map(({ to, label, icon: Icon, count }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    "sb-item" + (isActive ? " active" : "")
                  }
                  // §v3.route-prefetch (2026-05-07) — Linear-style
                  // hover prefetch. Hover the sidebar item and we
                  // start fetching that page's main query in the
                  // background. Click → page reads from warm cache
                  // and renders instantly.
                  //
                  // §v3.chunk-prefetch (2026-05-11) — also fire the
                  // lazy-loaded JS chunk on hover so the click→paint
                  // path doesn't trip the Suspense fallback at all.
                  // Combined with the AppShell-scoped Suspense
                  // boundary (no longer unmounts sidebar+header on
                  // chunk loads), this kills the "1-2 second blank
                  // flash" UX bug.
                  onMouseEnter={(e) => {
                    glideTo(e.currentTarget);
                    prefetchRoute(queryClient, to);
                    prefetchRouteChunk(to);
                  }}
                  onFocus={(e) => {
                    glideTo(e.currentTarget);
                    prefetchRoute(queryClient, to);
                    prefetchRouteChunk(to);
                  }}
                  onBlur={glideOff}
                >
                  <Icon className="ic" size={16} aria-hidden />
                  <span>{label}</span>
                  {/* §v4.nav-counts — live item count (1…99+). Hidden at 0
                      and while loading, so the rail never shows noise. */}
                  {count && typeof navCounts[to] === "number" && navCounts[to]! > 0 ? (
                    <span
                      className="count"
                      aria-label={`${navCounts[to]} items`}
                    >
                      {formatNavCount(navCounts[to]!)}
                    </span>
                  ) : null}
                  {to === "/settings" ? <SoftwareUpdateDot /> : null}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* ── Tenant profile card — the ONE place tenant accent shows */}
        <div className="sb-footer">
          {/* §ai-on-off — persistent AI answering switch, visible on every
              page above the profile card. One flag (agent_published), same
              one "Go live" and Mission Control write. */}
          <AgentPowerSwitch />
          <div className="sb-profile">
            <div className="avatar" aria-hidden>{initial}</div>
            <div className="info">
              <div className="who">
                {operator?.display_name ?? operator?.username ?? "Signed in"}
              </div>
              {/* §v3.profile-picker (2026-05-15) — show a human-readable
                  profile label based on the active tenant. Falls back
                  to the raw tenant slug for unknown tenants so the
                  caption never reads "undefined". */}
              <div className="brand">
                {tenantLabel}{" "}
                · {(operator?.role_name || "operator").toLowerCase()}
              </div>
            </div>
            <div className="actions">
              {/* §v3.profile-picker — return to the post-login picker
                  so the operator can flip between the workspaces they
                  belong to without signing out. */}
              <button
                type="button"
                onClick={() =>
                  impersonating ? void exitImpersonation() : navigate("/select-profile")
                }
                aria-label={impersonating ? "Exit to Mission Control" : "Switch profile"}
                title={impersonating ? "Exit to Mission Control" : "Switch profile"}
              >
                <Repeat2 size={14} strokeWidth={1.75} aria-hidden />
              </button>
              <ThemeToggle />
              {/* §pilot-review — quiet but findable: the same action row as
                  theme/sign-out, present on every page of the console. */}
              <button
                type="button"
                onClick={() => setReportOpen(true)}
                aria-label="Report a problem"
                title="Report a problem"
              >
                <LifeBuoy size={14} strokeWidth={1.75} aria-hidden />
              </button>
              <button
                type="button"
                onClick={handleLogout}
                aria-label="Sign out"
                title="Sign out"
              >
                <LogOut size={14} strokeWidth={1.75} aria-hidden />
              </button>
            </div>
          </div>
        </div>
      </aside>

      <main className="app-main">
        {/* §v4.tenant-isolation — persistent, always-visible (desktop +
            mobile) impersonation strip + one-click exit. Derived from
            localStorage so it survives a reload. */}
        {impersonating && (
          <div
            role="alert"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "9px 24px",
              fontSize: 13,
              lineHeight: 1.4,
              color: "var(--fg-1, inherit)",
              background: "color-mix(in oklab, oklch(72% 0.17 55) 16%, transparent)",
              borderBottom:
                "1px solid color-mix(in oklab, oklch(72% 0.17 55) 42%, var(--glass-hairline, rgba(255,255,255,0.12)))",
            }}
          >
            <Eye size={15} strokeWidth={1.75} aria-hidden />
            <span style={{ flex: 1 }}>
              Impersonating <strong>{tenantLabel}</strong> — acting as the tenant Owner.
              All actions are audited.
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => void exitImpersonation()}
            >
              Exit to Mission Control
            </button>
          </div>
        )}
        {/* §v3.mobile-shell — sticky top bar visible ONLY below
            the kit.css mobile breakpoint (768px). Carries the
            hamburger that opens the drawer + the brand wordmark
            so the operator never loses the brand context. */}
        <header className="app-topbar" aria-label="Mobile header">
          <button
            type="button"
            className="app-topbar-hamburger"
            onClick={() => setDrawerOpen((open) => !open)}
            aria-label={drawerOpen ? "Close menu" : "Open menu"}
            aria-expanded={drawerOpen}
            aria-controls="primary-nav-drawer"
          >
            {drawerOpen ? (
              <X size={22} strokeWidth={2} aria-hidden />
            ) : (
              <Menu size={22} strokeWidth={2} aria-hidden />
            )}
          </button>
          <div className="app-topbar-brand">
            <img
              className="app-topbar-brand-img"
              src={orikinWordmark}
              alt="ORIKIN AI"
              draggable={false}
            />
          </div>
          {/* §v5.mobile-ai-state — the AI on/off switch lives in the sidebar
              footer, which on a phone is behind the drawer. Surface the STATE
              here so "my assistant is off" is visible without opening a menu;
              tapping opens the drawer where the switch (and its confirmation)
              still lives. */}
          <AgentPowerStatus onOpen={() => setDrawerOpen(true)} />
        </header>

        {/* §E3 — credit-balance warning. Mounts at the top of the
            scroll column so every admin page carries it. Emits
            nothing when billing state is `ok`, so there's no
            layout cost in the healthy case. */}
        <CreditBalanceBanner />
        {/* §v3.no-flash-suspense (2026-05-11) — the previous design
            had ONE top-level <Suspense fallback={null}> wrapping the
            entire <AppRoutes>. When a lazy-loaded route chunk was
            fetched, the WHOLE AppShell (sidebar, header, this main
            element) unmounted because the Suspense boundary lived
            above it — and the fallback was literally `null`, so the
            screen went blank for the chunk-load duration (typically
            500-2000 ms on a cold network). Akil reported it as
            "site flashes blank for 1 or 2 seconds" 2026-05-11.

            Fix: scope the Suspense HERE so only the page content
            unmounts; the sidebar + header stay rendered through
            the transition. Fallback is an invisible div that
            preserves layout — no spinner, no message, no flash. */}
        <Suspense
          fallback={
            <div
              aria-busy="true"
              style={{ minHeight: "calc(100vh - 80px)" }}
            />
          }
        >
          <Outlet />
        </Suspense>
        <div className="notif-corner">
          <NotificationBell />
        </div>
        <NotificationToaster />
        <ReportProblemDialog open={reportOpen} onClose={() => setReportOpen(false)} />
      </main>
    </div>
  );
}
